import { Divider, Heading, RecordContent, RecordFooter, useTitleChange } from '@msaf/core-react'
import { useEffect, useState } from 'react'
import {
  CasingFieldsView,
  FormFieldsView,
  GalleryDetailsFieldsView,
  ScreenFieldsView,
} from '../../../../../types/form-types'
import NotesWrapperView from '../../../../components/well-forms/notes/notes-wrapper-view'
import WellConstructionCasingsView from '../../../../components/well-forms/well-construction/casings-view'
import WellConstructionDecommissioningView from '../../../../components/well-forms/well-construction/decommissioning-view'
import WellConstructionGalleryView from '../../../../components/well-forms/well-construction/gallery-details-view'
import WellConstructionScreensView from '../../../../components/well-forms/well-construction/screens-view'
import WellHeadConstructionView from '../../../../components/well-forms/well-construction/well-head-construction-view'
import WellViewButtons from '../../../../components/wells/well-view-buttons'
import { useGetWellData } from '../../../../hooks/use-api'
import { useWellId } from '../../../../hooks/use-well-id'
import { getRandomID } from '../../../../services/form-utils'

export default function ViewWellConstruction() {
  useTitleChange('View Well | Construction')
  const id = useWellId()
  const url = `wells/${id}/construction/view`
  const [fields, setFields] = useState<Partial<FormFieldsView>>({})
  const { isLoading, isSuccess, data } = useGetWellData(url)

  useEffect(() => {
    if (isSuccess && data.state) {
      setFields(data.state)
    }
  }, [isSuccess, data])

  return (
    <>
      <RecordContent>
        <Heading isSkeleton={isLoading} level={2}>
          Construction
        </Heading>
        {fields.galleryDetails ? (
          <>
            <Heading isSkeleton={isLoading} level={3}>
              Gallery details
            </Heading>
            {(!fields.galleryDetails || (Array.isArray(fields.galleryDetails) && !fields.galleryDetails.length)) && (
              <p>This well record has no gallery details</p>
            )}
            {Array.isArray(fields.galleryDetails) &&
              fields.galleryDetails.map((gallery: Partial<GalleryDetailsFieldsView>, index) => (
                <WellConstructionGalleryView
                  key={gallery.id || getRandomID()}
                  fields={gallery}
                  index={index}
                  isLoading={isLoading}
                />
              ))}
          </>
        ) : (
          <>
            <Heading isSkeleton={isLoading} level={3}>
              Casing details
            </Heading>
            {(!fields?.casings || (Array.isArray(fields.casings) && !fields.casings.length)) && (
              <p>This well record has no casings</p>
            )}
            {Array.isArray(fields.casings) &&
              fields?.casings.map((casing: Partial<CasingFieldsView>, index) => (
                <WellConstructionCasingsView
                  key={casing.id || getRandomID()}
                  fields={casing}
                  index={index}
                  isLoading={isLoading}
                />
              ))}

            <Divider />

            <Heading isSkeleton={isLoading} level={3}>
              Screen details
            </Heading>

            {(!fields?.screens || (Array.isArray(fields.screens) && !fields.screens.length)) && (
              <p>This well record has no screens</p>
            )}
            {Array.isArray(fields.screens) &&
              fields?.screens.map((screen: Partial<ScreenFieldsView>, index) => (
                <WellConstructionScreensView
                  key={screen.id || getRandomID()}
                  fields={screen}
                  index={index}
                  isLoading={isLoading}
                />
              ))}
          </>
        )}

        <Divider />

        <Heading isSkeleton={isLoading} level={3}>
          Well head construction
        </Heading>

        <WellHeadConstructionView fields={fields} isLoading={isLoading} />

        {fields.decommissioning && (
          <>
            <Divider />

            <Heading isSkeleton={isLoading} level={3}>
              Well decommissioning
            </Heading>

            {!Array.isArray(fields.decommissioning) && typeof fields.decommissioning === 'object' && (
              <WellConstructionDecommissioningView fields={fields.decommissioning} isLoading={isLoading} />
            )}
          </>
        )}
      </RecordContent>
      <RecordFooter
        notes={
          Array.isArray(fields.notes) &&
          !!fields.notes.length && <NotesWrapperView notes={fields.notes} isLoading={isLoading} />
        }
        areActionsFixed={true}
      >
        <WellViewButtons wellId={id} />
      </RecordFooter>
    </>
  )
}
